import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminOrders as mockOrders, AdminOrder, AdminOrderStatus } from '../../../mocks/adminOrders';
import { useOrders } from '../../../contexts/OrdersContext';
import AdminLayout from '../../../components/feature/AdminLayout';
import StatsCards from './components/StatsCards';
import OrdersTable from './components/OrdersTable';
import OrderDetailModal from './components/OrderDetailModal';

type FilterStatus = 'all' | AdminOrderStatus;
type DateFilter = 'all' | 'today' | 'week' | 'month' | 'custom';

const filterTabs: { value: FilterStatus; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'payment_pending', label: 'Awaiting Payment' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'on_way', label: 'On the Way' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

const dateTabs: { value: DateFilter; label: string; icon: string }[] = [
  { value: 'all', label: 'All Time', icon: 'ri-history-line' },
  { value: 'today', label: 'Today', icon: 'ri-sun-line' },
  { value: 'week', label: 'This Week', icon: 'ri-calendar-week-line' },
  { value: 'month', label: 'This Month', icon: 'ri-calendar-2-line' },
  { value: 'custom', label: 'Custom', icon: 'ri-calendar-line' },
];

function isInDateRange(dateStr: string, filter: DateFilter, customFrom: string, customTo: string): boolean {
  const date = new Date(dateStr);
  const now = new Date();
  if (filter === 'today') return date.toDateString() === now.toDateString();
  if (filter === 'week') {
    const start = new Date(now); start.setDate(now.getDate() - now.getDay()); start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setDate(start.getDate() + 6); end.setHours(23, 59, 59, 999);
    return date >= start && date <= end;
  }
  if (filter === 'month') return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  if (filter === 'custom') {
    const from = customFrom ? new Date(`${customFrom}T00:00:00`) : null;
    const to = customTo ? new Date(`${customTo}T23:59:59`) : null;
    if (from && date < from) return false;
    if (to && date > to) return false;
    return true;
  }
  return true;
}

function formatDateLabel(filter: DateFilter, from: string, to: string): string {
  const now = new Date();
  if (filter === 'today') return now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (filter === 'week') {
    const start = new Date(now); start.setDate(now.getDate() - now.getDay());
    const end = new Date(start); end.setDate(start.getDate() + 6);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} \u2013 ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  }
  if (filter === 'month') return now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  if (filter === 'custom' && from && to) return `${from} \u2013 ${to}`;
  if (filter === 'custom' && from) return `From ${from}`;
  return '';
}

export default function AdminOrdersPage() {
  const navigate = useNavigate();
  const { orders: contextOrders, pendingPayments, mockHidden, updateStatus, clearAllOrders } = useOrders();
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const customPickerRef = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [search, setSearch] = useState('');
  const [selectedOrder, setSelectedOrder] = useState<AdminOrder | null>(null);

  // Merge real orders with mock orders (mock orders shown unless hidden)
  const allOrders = useMemo(() => {
    const realIds = new Set(contextOrders.map((o) => o.id));
    if (mockHidden) return [...contextOrders];
    return [...contextOrders, ...mockOrders.filter((o) => !realIds.has(o.id))];
  }, [contextOrders, mockHidden]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (customPickerRef.current && !customPickerRef.current.contains(e.target as Node)) {
        setShowCustomPicker(false);
      }
    }
    if (showCustomPicker) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showCustomPicker]);

  const dateFilteredOrders = useMemo(() => {
    if (dateFilter === 'all') return allOrders;
    return allOrders.filter((o) => isInDateRange(o.createdAt, dateFilter, customFrom, customTo));
  }, [allOrders, dateFilter, customFrom, customTo]);

  const handleStatusChange = (id: string, status: AdminOrderStatus) => {
    updateStatus(id, status);
    if (selectedOrder?.id === id) {
      setSelectedOrder((prev) => prev ? { ...prev, status } : prev);
    }
  };

  const handleClearOrders = () => {
    clearAllOrders();
    setShowClearConfirm(false);
  };

  const handleDateTab = (val: DateFilter) => {
    setDateFilter(val);
    if (val === 'custom') setShowCustomPicker(true);
    else setShowCustomPicker(false);
  };

  const filteredOrders = useMemo(() => {
    let list = dateFilteredOrders;
    if (filter !== 'all') list = list.filter((o) => o.status === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (o) =>
          o.id.toLowerCase().includes(q) ||
          o.customer.toLowerCase().includes(q) ||
          o.address.toLowerCase().includes(q)
      );
    }
    return list;
  }, [dateFilteredOrders, filter, search]);

  const countByStatus = (s: FilterStatus) =>
    s === 'all' ? dateFilteredOrders.length : dateFilteredOrders.filter((o) => o.status === s).length;

  const dateLabel = formatDateLabel(dateFilter, customFrom, customTo);

  return (
    <AdminLayout>
      <div className="space-y-6">

        {/* Pending payments banner */}
        {pendingPayments.length > 0 && (
          <div className="flex items-center justify-between gap-4 bg-amber-50 border border-amber-200 rounded-xl px-5 py-3.5">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-amber-600 text-base" />
              </div>
              <div>
                <p className="text-sm font-bold text-amber-900">
                  {pendingPayments.length} order{pendingPayments.length > 1 ? 's' : ''} waiting for payment confirmation
                </p>
                <p className="text-xs text-amber-600 mt-0.5">
                  Approve these orders in the Payments page before expedition
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/payments')}
              className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold rounded-lg transition cursor-pointer whitespace-nowrap flex-shrink-0"
            >
              Review Now
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-stone-900">Orders</h1>
            <p className="text-sm text-stone-500 mt-0.5">Manage and update all customer orders</p>
          </div>
          <button
            onClick={() => setShowClearConfirm(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-xl transition cursor-pointer whitespace-nowrap"
          >
            <i className="ri-delete-bin-2-line" />
            Clear Orders
          </button>
        </div>

        {/* Date filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1 bg-white border border-stone-200 rounded-xl p-1">
            {dateTabs.map((tab) => {
              const active = dateFilter === tab.value;
              return (
                <div key={tab.value} className="relative" ref={tab.value === 'custom' ? customPickerRef : undefined}>
                  <button
                    onClick={() => handleDateTab(tab.value)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition cursor-pointer whitespace-nowrap ${
                      active ? 'bg-stone-800 text-white' : 'text-stone-500 hover:bg-stone-100'
                    }`}
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={`${tab.icon} text-xs`} />
                    </span>
                    {tab.label}
                  </button>

                  {tab.value === 'custom' && showCustomPicker && (
                    <div className="absolute top-full left-0 mt-2 z-30 bg-white border border-stone-200 rounded-2xl p-4 w-72">
                      <p className="text-xs font-bold text-stone-700 mb-3 uppercase tracking-wide">Select date range</p>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-stone-500 mb-1 block">From</label>
                          <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition cursor-pointer" />
                        </div>
                        <div>
                          <label className="text-xs text-stone-500 mb-1 block">To</label>
                          <input type="date" value={customTo} min={customFrom} onChange={(e) => setCustomTo(e.target.value)}
                            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition cursor-pointer" />
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <button onClick={() => { setCustomFrom(''); setCustomTo(''); setDateFilter('all'); setShowCustomPicker(false); }}
                          className="flex-1 px-3 py-2 text-xs font-medium text-stone-600 bg-stone-100 hover:bg-stone-200 rounded-lg transition cursor-pointer whitespace-nowrap">
                          Reset
                        </button>
                        <button onClick={() => setShowCustomPicker(false)} disabled={!customFrom && !customTo}
                          className="flex-1 px-3 py-2 text-xs font-bold text-white bg-orange-500 hover:bg-orange-600 disabled:opacity-40 rounded-lg transition cursor-pointer whitespace-nowrap">
                          Apply
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {dateFilter !== 'all' && dateLabel && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-xl">
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-calendar-check-line text-orange-500 text-xs" />
              </span>
              <span className="text-xs font-medium text-orange-700">{dateLabel}</span>
              <button onClick={() => { setDateFilter('all'); setCustomFrom(''); setCustomTo(''); }}
                className="w-3.5 h-3.5 flex items-center justify-center text-orange-400 hover:text-orange-600 transition cursor-pointer ml-0.5">
                <i className="ri-close-line text-xs" />
              </button>
            </div>
          )}
        </div>

        <StatsCards orders={dateFilteredOrders} />

        {/* Status filters + search */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-1 bg-white border border-stone-200 rounded-xl p-1 flex-wrap">
            {filterTabs.map((tab) => {
              const active = filter === tab.value;
              const count = countByStatus(tab.value);
              return (
                <button key={tab.value} onClick={() => setFilter(tab.value)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition cursor-pointer whitespace-nowrap ${
                    active ? 'bg-orange-500 text-white' : 'text-stone-500 hover:bg-stone-100'
                  }`}
                >
                  {tab.label}
                  <span className={`text-xs font-bold ${active ? 'text-orange-100' : 'text-stone-400'}`}>{count}</span>
                </button>
              );
            })}
          </div>

          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
              <i className="ri-search-line text-stone-400 text-sm" />
            </span>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search order ID, customer..."
              className="pl-9 pr-4 py-2 text-sm border border-stone-200 rounded-xl bg-white outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition w-64" />
          </div>
        </div>

        <p className="text-xs text-stone-400">
          Showing <strong className="text-stone-600">{filteredOrders.length}</strong> of {dateFilteredOrders.length} orders
          {dateFilter !== 'all' && <span className="text-orange-500 ml-1">&middot; filtered by date</span>}
        </p>

        <OrdersTable
          orders={filteredOrders}
          onSelectOrder={setSelectedOrder}
          onStatusChange={handleStatusChange}
        />
      </div>

      {selectedOrder && (
        <OrderDetailModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onStatusChange={handleStatusChange}
        />
      )}

      {showClearConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl w-full max-w-sm mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-delete-bin-2-line text-red-600 text-xl" />
              </div>
              <div>
                <h3 className="text-base font-bold text-stone-900">Clear all orders?</h3>
                <p className="text-sm text-stone-500 mt-0.5">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-sm text-stone-600 mb-6">
              All orders saved locally will be permanently deleted, including pending confirmations.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowClearConfirm(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-stone-700 bg-stone-100 hover:bg-stone-200 rounded-xl transition cursor-pointer whitespace-nowrap">
                Cancel
              </button>
              <button onClick={handleClearOrders}
                className="flex-1 px-4 py-2.5 text-sm font-bold text-white bg-red-500 hover:bg-red-600 rounded-xl transition cursor-pointer whitespace-nowrap">
                Yes, clear all
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
